/* @flow */
import * as React from 'react';
import Button from 'components/Button.jsx';
import Device from 'entities/Device';
import DeviceActions from 'actions/DeviceActions';
import ToastActions from 'actions/ToastActions';
import Form from 'components/Form.jsx';
import SettingsContainer from './SettingsContainer.jsx';
import FormInput from 'components/FormInput.jsx';
import CurrentUser from 'auth/CurrentUser';
import User from 'entities/User';
import EmailMergeConfirmation from 'user/components/EmailMergeConfirmation.jsx';
import { mountModal } from 'lib/modal';
import './styles/NewEmailDeviceForm.scss';

type Props = {
  onSubmit?: Function,
};

type State = {
  canSubmit: boolean,
  enteredEmail: string,
  errorMessage: string,
  hasChanged: boolean,
  isEnteringEmail: boolean,
  user: User,
};

const EXISTING_EMAIL_ERROR = 'email_exists';
const EVENT_SCREEN_NAME = 'settings_page';

class NewEmailDeviceForm extends React.Component<Props, State> {
  state: State = {
    canSubmit: false,
    enteredEmail: '',
    errorMessage: '',
    hasChanged: false,
    isEnteringEmail: false,
    user: CurrentUser.getUser(),
  };

  renderEmailMergeConfirmation = () => {
    return (
      <EmailMergeConfirmation
        oldUserEmail={this.state.enteredEmail}
        errorMessage={this.state.errorMessage}
        eventScreenName={EVENT_SCREEN_NAME}
        onDone={DeviceActions.loadDevices}
        // XXX should this really be required by EmailMergeConfirmation
        onRequestHide={() => null}
      />
    );
  };

  handleAddSuccess = (device: Device) => {
    this.setState({ isEnteringEmail: false, errorMessage: '' });
    ToastActions.showSuccessToast(`${device.getLabel()} added`);
  };

  handleAddError = (error: *) => {
    let errorMessage;

    if (error.field_errors) {
      errorMessage = error.field_errors
        .map(fieldError => fieldError.messages.join(', '))
        .join(', ');
    } else {
      // DeviceActions.newEmailDevice return deprecated validation
      errorMessage = error.message;
    }

    this.setState({ errorMessage });

    if (error.code === EXISTING_EMAIL_ERROR) {
      mountModal(this.renderEmailMergeConfirmation(), this);
      this.setState({ isEnteringEmail: false, errorMessage: '', enteredEmail: '' });
    } else {
      console.warn('NewEmailDeviceForm handleAddError', error);
      ToastActions.showAPIError(error);
    }
  };

  handleAddButtonClick = () => {
    this.setState({ isEnteringEmail: true });
  };

  handleCancelButtonClick = () => {
    this.setState({ isEnteringEmail: false, errorMessage: '', enteredEmail: '' });
  };

  handleSaveButtonClick = ({ email }: { email: string }) => {
    this.setState({ isEnteringEmail: false, errorMessage: '', enteredEmail: email });
    DeviceActions.newEmailDevice(email).then(res => {
      if (this.props.onSubmit) {
        this.props.onSubmit();
      }
      this.handleAddSuccess(res);
    }, this.handleAddError);
  };

  enableChanged = () => {
    this.setState({ hasChanged: true, errorMessage: '' });
  };

  disableButton = () => {
    this.setState({ canSubmit: false });
  };

  enableButton = () => {
    if (this.state.hasChanged) {
      this.setState({ canSubmit: true });
    }
  };

  render() {
    return (
      <Form
        key={this.state.user.cid}
        onChange={this.enableChanged}
        onValid={this.enableButton}
        onValidSubmit={this.handleSaveButtonClick}
        onInvalid={this.disableButton}>
        <SettingsContainer className="NewEmailDeviceForm">
          {this.state.isEnteringEmail ? (
            <div>
              <div className="NewEmailDeviceForm-addEmail">
                <FormInput
                  name="email"
                  type="email"
                  validations="isEmail"
                  placeholder="Enter an email address"
                  autofocus
                />
              </div>
              <Button
                size="small"
                type="submit"
                disabled={!this.state.canSubmit}
                eventElementName="NewEmailDeviceForm-save">
                Save
              </Button>
              <Button
                className="NewEmailDeviceForm-cancel"
                size="small"
                onClick={this.handleCancelButtonClick}
                level="secondary"
                eventElementName="NewEmailDeviceForm-cancel">
                Cancel
              </Button>
            </div>
          ) : (
            <Button size="large" level="text" color="brand" onClick={this.handleAddButtonClick}>
              Add an email address
            </Button>
          )}
        </SettingsContainer>
      </Form>
    );
  }
}

export default NewEmailDeviceForm;
